import { camelCase, last, sortBy } from 'lodash';
import moment from 'moment';
import { rgba } from 'polished';
import { ComponentProps, PropsWithChildren, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeColors } from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Callout, Card, Paragraph, Spinner, Subheading, Title } from '@treams/treams-ui';

import { AccountSwitcher } from '../navigation/TopBar';

const Container = styled(Box)<{ $modal?: boolean }>`
	${(props) => props.$modal && 'overflow-y: auto;'}
`;

const planColors: { [K in graphql.WorkspacePlanType]: keyof ThemeColors } = {
	[graphql.WorkspacePlanType.Pro]: 'primary',
	[graphql.WorkspacePlanType.Conversations]: 'employee',
	[graphql.WorkspacePlanType.Engage]: 'manager',
	[graphql.WorkspacePlanType.Classic]: 'primaryLight',
	[graphql.WorkspacePlanType.Ai]: 'primaryLight',
};

const Product = styled(Card)<{ type: graphql.WorkspacePlanType }>`
	border-top: 8px solid ${(props) => props.theme.colors[planColors[props.type]]};
	border-radius: 8px;

	${(props) =>
		props.type === graphql.WorkspacePlanType.Pro &&
		`
        box-shadow:
            0px 0px 0px 1px ${props.theme.colors[planColors[props.type]]},
            0px 0px 0px 3px ${rgba(props.theme.colors[planColors[props.type]], 0.25)}
    `}
`;

type FeatureKey =
	| 'pulseSurveys'
	| 'MTO'
	| 'customQuestions'
	| '360Feedback'
	| 'conversations'
	| 'skillsMatrix'
	| 'developmentPlans'
	| 'goals'
	| 'talentReviews'
	| 'learning'
	| 'analytics';

const planFeatures: { [K in graphql.WorkspacePlanType]: FeatureKey[] } = {
	[graphql.WorkspacePlanType.Pro]: [
		'pulseSurveys',
		'MTO',
		'customQuestions',
		'360Feedback',
		'conversations',
		'skillsMatrix',
		'developmentPlans',
		'goals',
		'talentReviews',
		'learning',
		'analytics',
	],
	[graphql.WorkspacePlanType.Conversations]: ['360Feedback', 'conversations', 'goals', 'analytics'],
	[graphql.WorkspacePlanType.Engage]: ['pulseSurveys', 'MTO', 'customQuestions', 'analytics'],
	[graphql.WorkspacePlanType.Classic]: [],
	[graphql.WorkspacePlanType.Ai]: [],
};

type ProductPlanView = Pick<graphql.WorkspacePlanV2, 'isTrial' | 'isExpired' | 'endDate'>;

function ProductPlan(
	props: PropsWithChildren<{
		type: graphql.WorkspacePlanType;
		plans: ProductPlanView[];
	}>,
) {
	const { t } = useTranslation();
	const translationKey = camelCase(props.type);

	const hasContinuousPlan = props.plans.some((plan) => !plan.endDate);
	const lastFinitePlan = last(
		sortBy(
			props.plans.filter((plan) => !!plan.endDate),
			(plan) => moment(plan.endDate).valueOf(),
		),
	);

	const planName = t(`workspaces.plans.type.${translationKey}`);

	return (
		<Product type={props.type}>
			<Subheading>Treams {planName}</Subheading>
			<Paragraph mb={3}>{t(`workspaces.plans.description.${translationKey}`)}</Paragraph>
			{planFeatures[props.type].map((feature, idx) => (
				<Paragraph key={idx}>
					✅&nbsp; {t(`workspaces.plans.featureDescription.${feature}`)}
				</Paragraph>
			))}
			{!hasContinuousPlan &&
				lastFinitePlan &&
				(lastFinitePlan.isExpired ? (
					<Callout color="danger" mt={3}>
						{t(
							`workspaces.plans.expiry.${lastFinitePlan.isTrial ? 'trial' : 'subscription'}.past`,
							{
								type: planName,
							},
						)}
					</Callout>
				) : (
					<Callout color="warning" mt={3}>
						{t(
							`workspaces.plans.expiry.${lastFinitePlan.isTrial ? 'trial' : 'subscription'}.future`,
							{
								type: planName,
								count: moment(lastFinitePlan.endDate).diff(moment(), 'days'),
							},
						)}
					</Callout>
				))}
		</Product>
	);
}

export default function Billing(props: { modal?: boolean }) {
	const query = graphql.useViewerWorkspacePlanQuery();

	const { t } = useTranslation('', { keyPrefix: 'settings.billing' });

	const planCards = Object.fromEntries(
		Object.keys(graphql.WorkspacePlanType).map(
			(planTypeKey: keyof typeof graphql.WorkspacePlanType) => {
				const planType = graphql.WorkspacePlanType[planTypeKey];
				const plans = (query.data?.viewer.workspace.plans ?? []).filter(
					(plan) => plan.type === planType,
				);

				const planComponent = <ProductPlan key={planTypeKey} type={planType} plans={plans} />;

				return [planType, planComponent];
			},
		),
	) as { [K in graphql.WorkspacePlanType]: ReactElement };

	const sizeProps: ComponentProps<typeof Box> = { width: '33%', maxWidth: 275 };

	return (
		<Container pt={4} $modal={props.modal}>
			<Box px={4}>
				{props.modal ? (
					<Flex mb={3} alignItems="center">
						<Title flexGrow={1} flexShrink={1}>
							{t('modal.title')}
						</Title>
						<Box ml={2} flexGrow={0} flexShrink={0}>
							<AccountSwitcher />
						</Box>
					</Flex>
				) : (
					<Title mb={3}>{t('title')}</Title>
				)}
				<Paragraph>{t('description')}</Paragraph>
				{query.loading ? (
					<Spinner mt={4} />
				) : (
					<Flex mt={4}>
						<Box mr={4} {...sizeProps}>
							<Subheading>{t('recommendation.heading')}</Subheading>
							<Paragraph mb={4}>{t('recommendation.description')}</Paragraph>
							{planCards.PRO}
						</Box>
						<Box flexGrow={1}>
							<Subheading>{t('alternatives.heading')}</Subheading>
							<Paragraph width={300} mb={4}>
								{t('alternatives.description')}
							</Paragraph>
							<Flex>
								<Box {...sizeProps} mr={3}>
									{planCards.CONVERSATIONS}
								</Box>
								<Box {...sizeProps}>{planCards.ENGAGE}</Box>
							</Flex>
						</Box>
					</Flex>
				)}
			</Box>
		</Container>
	);
}
